import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as XLSX from "xlsx";
import Button from "../../components/utils/Button";
import Table from "../../components/utils/Table";
import { downloadExcelSampleFile } from "../../helpers/commonFunction";
import CardLayout from "../../components/utils/CardLayout";
import ToastAlert from "../../components/utils/ToastAlert";
import cancelWifiActions from "../../redux/wifiActivation/actions";
import { useDispatch, useSelector } from "react-redux";

const { cancelWiFiActivation, resetMessage } = cancelWifiActions;

const MAX_RECORDS = 100;
const CancelWiFi = () => {
  const dispatch = useDispatch();
  const [invailidData, setInvailidData] = useState(0);
  const [previewData, setPreviewData] = useState([]);
  const [previewColumn, setPreviewColumn] = useState([]);
  const [vailidError, setVailidError] = useState("");
  const { cancelWifiActivationLoading, cancelWifiActivationMessage, cancelWifiActivationError } = useSelector((state) => state.wifiActivation);

  const { handleSubmit, reset, formState: { isSubmitting }, } = useForm({ mode: "onChange", defaultValues: {}, });
  useEffect(() => { return () => { dispatch(resetMessage()); } }, []);

  useEffect(() => {
    if (cancelWifiActivationMessage) {
      ToastAlert({ type: "success", text: cancelWifiActivationMessage });
      handleReset();
    }
    if (cancelWifiActivationError) {
      ToastAlert({ type: "error", text: cancelWifiActivationError })
    }
  }, [cancelWifiActivationMessage, cancelWifiActivationError]);

  const downloadSampleFile = () => {
    const data = [{  "SerialNumber": '1234567890123456789',
        "MSISDN": "1234567890", },];

    downloadExcelSampleFile(data, "Wi-Fi-Cancel-Activation.xlsx");
  };

  const onFileUpload = (e) => {
    const fileReader = new FileReader();
    const file = e.target.files?.[0];
    setVailidError("");
    setPreviewColumn([]);
    setPreviewData([]);
    setInvailidData(0);
    if (!file) return;
    // Validate file type
    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      setVailidError("Please upload a valid file (.xlsx, .xls)");
      return;
    }
    fileReader.readAsArrayBuffer(file);
    fileReader.onload = (event) => {
      const data = new Uint8Array(event.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, {
        defval: "",
      });
      const headers = Object.keys(jsonData[0] || {});
      const requiredColumns = ["SerialNumber", "MSISDN", ];
      const isValid =
        headers.length === 2 &&
        requiredColumns.every((col) => headers.includes(col));
      if (!isValid) { setVailidError("Invalid file. required columns are serialnumber and MSISDN."); return; }
      if (jsonData?.length > MAX_RECORDS) {
        setVailidError(`Maximum ${MAX_RECORDS} records allowed.`);
        return;
      } const heading = [
        ...headers.map((id) => ({ displayName: `${id}`, key: `${id}`, })), {
          displayName: "Remarks", key: "remark", },
        { displayName: "Status", key: "status", textAlign: true, },
      ];
      setPreviewColumn(heading);
      const validatedData = validateData(jsonData) || [];
      const invailidDataFilter = validatedData.filter((item) => item?.validFlag === 0);
      setInvailidData(invailidDataFilter?.length); setPreviewData(validatedData);
    };
  };

  const validateData = (rows) => {
    const serialSeen = new Set();
    const msisdnSeen = new Set();

    return rows.map((row) => {
      let remark = [];
      let validFlag = 1;

      const serial = String(row["SerialNumber"] || "").trim();
      const msisdn = String(row["MSISDN"] || "").trim();

     if (!serial) {
        remark.push("Serial number is missing");
        validFlag = 0;
      }
      else if (!msisdn) {
        remark.push("MSISDN is missing");
        validFlag = 0;
      }
      else if (!/^\d+$/.test(msisdn)) {
        remark.push("MSISDN must be numeric");
        validFlag = 0;
      }
      else if (msisdn.length !== 10) {
        remark.push("MSISDN must be 10 digits");
        validFlag = 0;
      }
      else if (msisdnSeen.has(msisdn)) {
        remark.push("Duplicate MSISDN number");
        validFlag = 0;
      }
      else if (!/^\d+$/.test(serial)) {
        remark.push("Serial number must be numeric");
        validFlag = 0;
      } else if (serial.length !== 19) {
        remark.push("Serial number must be 19 digits");
        validFlag = 0;
      } else if (serialSeen.has(serial)) {
        remark.push("Duplicate serial number");
        validFlag = 0;
      } else {
        serialSeen.add(serial);
        msisdnSeen.add(msisdn)
      }

      return {
         ...row,
        SerialNumber: serial,
        validFlag,
        remark: remark.length ? remark.join(", ") : "Valid",
      };
    });
  };
  const rowData = useMemo(() => {
    if (previewData && previewData.length > 0) {
      return previewData.map((item) => ({
        ...item,
        status:
          item.validFlag === 1 ? (
            <div className='text-success d-flex align-items-center justify-content-center'>
              <i className="ri-checkbox-circle-fill fs-6"></i> <span className="fw-semibold">Valid</span>
            </div>
          ) : (
            <div className="text-danger d-flex align-items-center justify-content-center">
              <i className="ri-close-circle-fill fs-6"></i><span className="fw-semibold">Invalid</span>
            </div>
          ),
      }));

    }
    return [];
  }, [previewData]);

  const onSubmit = () => {
     const payload = rowData && rowData.length > 0 && rowData.map((item) => {
      return {
        "iccid": item?.SerialNumber,
        "msisdn": String(item?.MSISDN),
      };
    });
    dispatch(cancelWiFiActivation(payload));
  };
  

  const handleReset = () => {
    reset();
    setPreviewData([]);
    setPreviewColumn([]);
    const fileInput =
      document.getElementById("wifiExcelFile");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  return (
    <CardLayout
      title="Cancel Wi-Fi"
      icon="fas fa-ban"
      rightContent={
        <Button
          title="Sample File"
          buttonType="button"
          icon={
            <i className="ri-download-cloud-line me-1" />
          }
          variant="secondary"
          style={{
            padding: "5px 10px",
          }}
          onClick={downloadSampleFile}
        /> } >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="row">
          <div className="form-group col-lg-3 col-md-4">
            <label className="form-label fw-semibold">
              Upload File <span className="text-danger">*</span>
              <span
                    className="position-relative ms-2 info-icon-wrapper"
                    style={{ cursor: "pointer" }}
                  >
                    <i className="fas fa-info-circle text-primary fs-5 info-icon-text" />

                    <div className="dealer-confirm-popup position-absolute bg-white border rounded shadow-sm py-2">
                      <ul className="mb-0 ps-4 custom-info-list">
                        <li className="mb-1"> Maximum {MAX_RECORDS} records are allowed per submission. </li>
                        <li className="mb-1"> Duplicate Serial Numbers/MSISDN are not allowed. </li>
                        <li className="mb-1"> All fields are required. </li>
                      </ul>
                    </div>
                  </span>
            </label>
            <input
              type="file"
              accept=".xlsx,.xls"
              className="form-control"
              id="fileUpload"
              onChange={onFileUpload}
            />
            {vailidError && (<div className="" style={{ color: "#dc3545", fontSize: "12px", marginTop: "2px", }} > {vailidError} </div>)}
          </div>

          {/* Actions */}
          <div className="col-md-6 col-lg-4 d-flex align-items-end" style={{ height: '70px' }}>
            <div className="d-flex gap-3">
              <Button
                title="Cancel Activation"
                buttonType="submit"
                variant="primary"
                loading={cancelWifiActivationLoading}
                icon={<i className="ri-send-plane-fill me-2"></i>}
                disable={(invailidData > 0) || rowData?.length == 0}
              />

              <Button
                title="Reset"
                buttonType="button"
                variant="secondary"
                disable={cancelWifiActivationLoading}
                icon={<i className="ri-refresh-line me-2"></i>}
                onClick={handleReset}
              />
            </div>
          </div>

          {/* Details */}
          {rowData && rowData.length > 0 && <div className="col-lg-5 col-md-5 d-flex align-items-end" style={{ height: '70px' }}>
            <div className="border rounded-3 bg-white shadow-sm px-3 py-2 w-100">
              <div className="d-flex align-items-center justify-content-between">

                {/* Total */}
                <div className="d-flex align-items-center gap-2">
                  <i className="fa-solid fa-boxes-stacked text-primary"></i>
                  <span className="text-muted small">Total:</span>
                  <span className="fw-bold text-primary">
                    {rowData?.length ?? 0}
                  </span>
                </div>

                <div className="border-start" style={{ height: "22px" }} />

                {/* Valid */}
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted small">Valid:</span>
                  <span className="fw-bold text-success">
                    {Math.max(
                      0,
                      (rowData?.length || 0) - (invailidData || 0)
                    )}
                  </span>
                </div>

                <div className="border-start" style={{ height: "22px" }} />

                {/* Invalid */}
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted small">Invalid:</span>
                  <span className="fw-bold text-danger">
                    {invailidData || 0}
                  </span>
                </div>

              </div>
            </div>
          </div>}
        </div>
      </form>
      {rowData && rowData.length > 0 && (<div className="mt-3">
        <Table
          columns={previewColumn}
          loading={false}
          data={rowData}
          pagination={true}
          tableHeight="330px"
        />
      </div>
      )}
    </CardLayout>
  );
};

export default CancelWiFi;
